import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  getFixtureStaticFullWithSubscriptions,
  applyStaticDataPatch,
  eventsUpdatedPreset,
  staticDataUpdatedPreset,
  seasonAllFixturesPreset,
  readAllFixturesNotice,
} from "@aelive/dds-client";
import { dds } from "../dds/ddsClient";
import { useDDSGetData } from "../dds/useDDSGetData";
import { stripTombstonedEvents } from "../utils/eventTombstones";
import {
  applySiblingRows as mergeSiblingRows,
  recordSiblingRow as storeSiblingRow,
} from "./siblingFixtureRows";
import { useTabResync } from "@bb/event-service";


/**
 * Season-wide all_fixtures notices (OPUS-1489).
 *
 * `staticDataUpdated` takes a REQUIRED fixture_id, so the fixture-scoped
 * subscription below only ever hears about THIS fixture. An operator editing a
 * SIBLING fixture still changes the season list this page holds, and a BULK
 * import notice carries no fixture_id at all, so it is invisible there by
 * construction.
 *
 * Written out rather than using the packaged preset: the installed
 * @aelive/dds-client still declares fixture_id as String! in the document even
 * though the DDS schema relaxed it to optional for exactly this consumer.
 *
 * competition_id is optional server-side and additive — supplying it holds the
 * notice to BOTH halves of the scope.
 */
/**
 * How long to let all_fixtures notices settle before re-reading the season.
 *
 * 750ms, matching Aether's LIVE_REFRESH_DEBOUNCE_MS, so the two apps listening
 * to this channel settle on the same cadence. The read it guards is 380
 * fixtures wide, which is why it is worth coalescing at all.
 */
const SEASON_NOTICE_DEBOUNCE_MS = 750;

/**
 * Ask season.all_fixtures for the WHOLE season — not a guess at how big one is.
 *
 * This was 380, chosen as "a full Premier League season". The guess was already
 * wrong in production: Premier League 2025/26 holds 383 all_fixtures rows, so
 * three fixtures were silently missing from this hook's season list — and from
 * the caption data-binding context it feeds. OPUS-1528 makes it worse in the
 * most confusing way available: add one cross-competition fixture to a
 * 380-fixture season and the fixture just created is the one that disappears.
 *
 * The truncation is silent. DDS's selectAllFixturesWindow simply slices, so a
 * cut-off season is indistinguishable from a season that small.
 *
 * 500 is DDS's own ALL_FIXTURES_MAX_LIMIT — it clamps to that whatever is sent,
 * so this asks for exactly as much as the node can ever return and cannot run
 * away. A season above 500 is still cut off; that ceiling is DDS's (both its
 * window and its DWS read) and moving it is a DDS change, not one made here.
 */
const SEASON_ALL_FIXTURES_LIMIT = 500;

const SEASON_ALL_FIXTURES_UPDATED = `
  subscription SeasonAllFixturesUpdated($season_id: String!, $competition_id: String) {
    seasonFixtureResultsUpdated(season_id: $season_id, competition_id: $competition_id) {
      collection
      key
      data
      op
    }
  }
`;

export const useFixtureData = (competitionId, seasonId, fixtureId, { onLineupChange } = {}) => {
  const [fixtureState, setFixtureState] = useState({});
  const [loading, setLoading] = useState(true);
  // Bump on tab-return to force the subscription effect to tear down +
  // re-establish. Chrome may close the graphql-ws connection silently when
  // the tab is backgrounded (no keepAlive on the dds-client side); we
  // re-fetch + re-subscribe on focus to guarantee a fresh snapshot.
  const [resyncToken, setResyncToken] = useState(0);
  // OPUS-1489: bumped when an all_fixtures change notice arrives for this
  // season, so the season side-fetch below re-reads. Separate from resyncToken
  // because that one re-runs the whole 281-key fixture fetch; an all_fixtures
  // change only needs the season list re-read.
  const [allFixturesToken, setAllFixturesToken] = useState(0);
  const patchFnRef = useRef(null);
  const localScoreRef = useRef(null);
  // Stable ref for the lineup-change callback so the subscription effect
  // doesn't tear down + reconnect every time the parent re-renders.
  const onLineupChangeRef = useRef(onLineupChange);
  useEffect(() => { onLineupChangeRef.current = onLineupChange; }, [onLineupChange]);
  // Tombstone set: any event_id removed via updateFixtureState stays
  // filtered out of every subsequent subscription emit. Needed because
  // DWS / importer pipelines often re-publish events through the static
  // channel after a soft-delete, which otherwise resurrects them in the
  // library's currentData and feeds them back into the UI / Maestro state.
  const removedEventIdsRef = useRef(new Set());

  const stripRemovedEvents = useCallback(
    (data) => stripTombstonedEvents(data, removedEventIdsRef.current),
    [],
  );

  /**
   * OPUS-1505 — sibling fixtures in our season.fixture window.
   *
   * The merge itself lives in ./siblingFixtureRows (pure, unit-tested); these
   * are the ref that holds the rows and the two bindings over it. See that
   * module for why this cannot go through applyStaticDataPatch.
   *
   * Held in a ref and re-applied on every emit rather than written once,
   * because onUpdate rebuilds from the library's currentData and ignores
   * `prev` — a one-shot write would be wiped by the next patch on our own
   * fixture. Same shape as removedEventIdsRef / localScoreRef above. Bounded by
   * the window (~30 entries) and cleared whenever the main fetch effect
   * re-runs, so an authoritative refetch is never overwritten by an older
   * merge.
   */
  const siblingRowsRef = useRef(new Map());

  const applySiblingRows = useCallback(
    (data) => mergeSiblingRows(data, siblingRowsRef.current),
    [],
  );

  const recordSiblingRow = useCallback(
    (payload) => storeSiblingRow(siblingRowsRef.current, payload, fixtureId),
    [fixtureId],
  );

  // Re-splice a recent local optimistic score (localScoreRef) over incoming
  // data so a stale fixture_result — from either a live subscription emit or
  // a resync snapshot fetch that raced the server-side score merge — can't
  // clobber a score we just set ourselves. Shared by both read paths below;
  // only the subscription path used to apply this, which left the resync
  // path free to silently revert an unwound goal's score (OPUS bug).
  const applyLocalScoreGuard = useCallback((candidateData) => {
    const local = localScoreRef.current;
    if (!local) return candidateData;
    const now = Date.now();
    if (!local.timestamp || now - local.timestamp >= 5000) {
      localScoreRef.current = null;
      return candidateData;
    }
    const { timestamp, ...localFields } = local;
    const baseFr =
      candidateData?.fixture_result?.[0] && typeof candidateData.fixture_result[0] === 'object'
        ? candidateData.fixture_result[0]
        : {};
    return {
      ...candidateData,
      fixture_result: [
        { ...baseFr, ...localFields },
        ...(candidateData?.fixture_result?.slice(1) || []),
      ],
    };
  }, []);

  // Track the fixture key (and effect run count) so we can tell apart
  //   - initial mount / fixture change → show the Loader (no data yet)
  //   - resync (tab return) → keep the existing tree mounted and refetch
  //     silently so open dialogs (e.g. Maestro) don't get unmounted and
  //     lose their state.
  const lastFetchKeyRef = useRef(null);

  useEffect(() => {
    if (!competitionId || !seasonId || !fixtureId) {
      setLoading(false);
      setFixtureState({});
      lastFetchKeyRef.current = null;
      return;
    }

    const currentKey = `${competitionId}|${seasonId}|${fixtureId}`;
    const isFixtureChange = lastFetchKeyRef.current !== currentKey;
    lastFetchKeyRef.current = currentKey;

    // Only reset tombstones when the fixture actually changes — a resync
    // for the same fixture must preserve them, otherwise a recently-deleted
    // event would resurrect on the silent refetch.
    if (isFixtureChange) {
      removedEventIdsRef.current = new Set();
    }

    // Unlike tombstones, sibling rows are dropped on RESYNC as well as on a
    // fixture change: this effect is about to refetch the whole season window
    // from DDS, and that response is authoritative. Keeping an older merged
    // value on top of it could only ever pin a stale score.
    siblingRowsRef.current = new Map();

    let cancelled = false;
    let unsubscribe = null;

    // Show the Loader on initial mount and fixture change only. Resync
    // is a background refresh; flipping loading=true here would re-render
    // TXLive into the Loader branch, unmounting every dialog.
    if (isFixtureChange) {
      setLoading(true);
    }

    getFixtureStaticFullWithSubscriptions(
      dds,
      {
        competition_id: competitionId,
        season_id: seasonId,
        fixture_id: fixtureId,
      },
      (updatedData) => {
        if (cancelled) return;
        // Subscription occasionally emits null/undefined when the underlying
        // collection has nothing for the fixture. Don't blow up the tree.
        if (!updatedData || typeof updatedData !== 'object') return;
        // Re-strip tombstoned events — DWS will sometimes re-publish a
        // recently-deleted event before the source has propagated the
        // delete; without this guard the row reappears in the UI / caption.
        updatedData = stripRemovedEvents(updatedData);
        // Re-apply sibling rows: this snapshot came from the library's
        // currentData, which has never seen them.
        updatedData = applySiblingRows(updatedData);
        setFixtureState((prev) => {
          const guarded = applyLocalScoreGuard(updatedData);
          if (guarded !== updatedData) {
            // Narrow update — only fixture_result + array[0] get new
            // identity, everything else keeps its ref so downstream
            // memos (home_team, lineup, events) don't re-fire (P4).
            return guarded;
          }

          // localScoreRef is not active — sync top-level fixture_result from the
          // nested season/fixture path so DDS patches (which only update
          // season[].fixture[].fixture_result) are reflected in the top-level
          // fixture_result the UI reads from.
          //
          // season[].fixture[] holds EVERY fixture in the season. We MUST
          // pick the one whose fixture_id matches the one we're loaded for —
          // taking [0] grabs the first sibling and silently pastes its score
          // onto the current fixture (e.g. on clock_start the subscription
          // update re-runs this code and the UI suddenly inherits another
          // match's 1-1).
          const matchingSeasonFixture = fixtureId
            ? (updatedData?.season?.[0]?.fixture || []).find((f) => f?.fixture_id === fixtureId)
            : null;
          const nestedFR = matchingSeasonFixture?.fixture_result;
          if (nestedFR != null) {
            const frObj = Array.isArray(nestedFR) ? nestedFR[0] : nestedFR;
            if (frObj && typeof frObj === 'object') {
              // Same narrow update as above.
              const baseFr =
                updatedData.fixture_result?.[0] && typeof updatedData.fixture_result[0] === 'object'
                  ? updatedData.fixture_result[0]
                  : {};
              return {
                ...updatedData,
                fixture_result: [
                  { ...baseFr, ...frObj },
                  ...(updatedData.fixture_result?.slice(1) || []),
                ],
              };
            }
          }

          return updatedData;
        });
      },
      // OPUS-1489 — the refetch signal.
      //
      // An all_fixtures notice carries NO VALUES by design, so there is nothing
      // to merge: the library recognises it (dds-client 0.8.5) and hands it here
      // instead of letting it fall through to a no-op patch, which is what used
      // to happen. Before that, a score edit in Aether or a Live flip on another
      // Drive was delivered to this subscriber and silently discarded.
      //
      // Guard on the season, not the fixture: the season list is what this
      // re-reads, and a BULK import notice carries no fixture_id at all, so a
      // fixture-level check would drop the very case that matters most.
      (notice) => {
        if (cancelled) return;
        if (notice?.season_id && notice.season_id !== seasonId) return;
        setAllFixturesToken((t) => t + 1);
      },
    )
      .then(({ initialData, unsubscribeAll, handlePatch }) => {
        if (cancelled) {
          unsubscribeAll();
          return;
        }
        unsubscribe = unsubscribeAll;
        patchFnRef.current = handlePatch;
        // Strip tombstones in case the initial fetch raced a delete that
        // happened on a prior fixture mount. Also re-apply the local score
        // guard — a resync (tab focus after backgrounding) can land a
        // fixture_result snapshot fetched before the server finished
        // merging a just-issued score patch (e.g. mid goal-unwind), which
        // would otherwise silently revert an already-correct local score.
        if (initialData && typeof initialData === 'object') {
          setFixtureState(applySiblingRows(applyLocalScoreGuard(stripRemovedEvents(initialData))));
        } else if (isFixtureChange) {
          // Initial load or fixture change with no data — clear so callers
          // see an empty (not stale) tree. On resync, prefer the existing
          // tree over an empty refetch response.
          setFixtureState({});
        }
        setLoading(false);
      })
      .catch((error) => {
        if (cancelled) return;
        console.error("Failed to load fixture static data:", error);
        // Only clear fixtureState on the initial-load error path. Clearing
        // it during a resync failure would blank a live fixture the
        // operator can still see — better to keep the (stale) data and
        // try again next visibility change.
        if (isFixtureChange) {
          setFixtureState({});
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
      if (unsubscribe) unsubscribe();
    };
  }, [competitionId, seasonId, fixtureId, resyncToken]);


  useEffect(() => {
    if (!fixtureId) return;
    const trackDelete = (patch) => {
      if (!patch || patch.collection !== 'event') return;
      const op = String(patch.op ?? '').toLowerCase();
      if (op !== 'delete') return;
      const id = patch.key?.event_id || patch.data?.event_id;
      if (!id) return;
      removedEventIdsRef.current.add(id);
    };
    // Patch-level lineup-activity detector. Fires onLineupChange the moment
    // a lineup patch shows up on either DDS stream — well before the
    // aggregated onUpdate snapshot path settles. Leading-edge 5s debounce
    // collapses the ~36 patches of a Pull-Lineup burst into a single toast.
    let lastLineupFire = 0;
    let resyncTimer = null;
    const detectLineupActivity = (patch) => {
      if (patch?.collection !== 'lineup') return;

      // Trailing-edge 500ms debounce: fires once after the burst (or single patch)
      // settles. Only then do we clear the sticky cache and resync, so the guard
      // never sees a degenerate mid-burst intermediate state with an empty cache.
      const teamId = patch?.key?.team_id;
      clearTimeout(resyncTimer);
      resyncTimer = setTimeout(() => {
        onLineupChangeRef.current?.({ teamId, cacheOnly: true });
        setResyncToken(n => n + 1);
      }, 500);

      const now = Date.now();
      if (now - lastLineupFire < 5000) return;
      lastLineupFire = now;
      console.log('[lineup-pull] activity detected', { op: patch?.op, key: patch?.key });
      onLineupChangeRef.current?.({ teamId });
    };
    const unsubEvents = dds.subscribe(
      eventsUpdatedPreset({ fixture_id: fixtureId }),
      (msg) => {
        trackDelete(msg?.eventsUpdated);
        detectLineupActivity(msg?.eventsUpdated);
      },
    );
    const unsubStatic = dds.subscribe(
      staticDataUpdatedPreset({ fixture_id: fixtureId }),
      (msg) => {
        trackDelete(msg?.staticDataUpdated);
        detectLineupActivity(msg?.staticDataUpdated);
      },
    );
    // Season-wide: any all_fixtures change in THIS season re-reads the season
    // list, not just a change to the fixture on screen.
    //
    // OPUS-1505 — this callback used to ignore its own message entirely:
    //
    //   () => setAllFixturesToken((t) => t + 1)
    //
    // It selects collection/key/data/op and discarded all four. That matters
    // because seasonFixtureResultsUpdated is NOT an all_fixtures-only channel —
    // DDS delivers `fixture_result` and `fixture` payloads on it too
    // (subscription.resolvers.js). So every goal anywhere in the season, in any
    // open Drive, triggered a fresh 380-fixture season read. The sibling
    // all_fixtures path a few lines up was already guarded; only this one was
    // not, because the library had already run readAllFixturesNotice for that one.
    //
    // Two guards and a debounce, in that order:
    //   1. it must actually BE an all_fixtures notice — readAllFixturesNotice
    //      returns null for a fixture_result or fixture payload;
    //   2. it must be THIS season. Kept season-scoped rather than fixture-scoped
    //      on purpose: a BULK import notice carries no fixture_id, so a
    //      fixture-level check would drop the case that matters most;
    //   3. debounce, so a score flurry or a bulk import coalesces into one read
    //      instead of one per message.
    let unsubSeason = null;
    let seasonNoticeTimer = null;
    if (seasonId) {
      unsubSeason = dds.subscribe(
        {
          query: SEASON_ALL_FIXTURES_UPDATED,
          variables: { season_id: seasonId, competition_id: competitionId ?? null },
        },
        (msg) => {
          // No `cancelled` flag here: this effect unsubscribes and clears the
          // timer in its cleanup, so nothing can arrive or fire after teardown.
          const payload = msg?.seasonFixtureResultsUpdated;
          const notice = readAllFixturesNotice(payload);
          if (!notice) {
            // OPUS-1505 — NOT a notice, so it is a live fixture_result/fixture
            // row for some fixture in this season, and this is the only place
            // one is ever delivered to us for a fixture other than our own.
            // It used to be dropped here, which is what left sibling scores in
            // season.fixture stale until a refetch. Everything needed is
            // already on the wire: the subscription selects collection/key/
            // data/op.
            //
            // No debounce: unlike the notice path this triggers no read, just a
            // targeted merge that returns the previous state by identity when
            // the fixture is outside our window.
            if (recordSiblingRow(payload)) {
              setFixtureState((prev) => applySiblingRows(prev));
            }
            return;
          }
          if (notice.season_id && notice.season_id !== seasonId) return;
          clearTimeout(seasonNoticeTimer);
          seasonNoticeTimer = setTimeout(
            () => setAllFixturesToken((t) => t + 1),
            SEASON_NOTICE_DEBOUNCE_MS,
          );
        },
      );
    }

    return () => {
      clearTimeout(resyncTimer);
      clearTimeout(seasonNoticeTimer);
      try { unsubEvents?.(); } catch (e) { /* ignore */ }
      try { unsubStatic?.(); } catch (e) { /* ignore */ }
      try { unsubSeason?.(); } catch (e) { /* ignore */ }
    };
  }, [fixtureId, seasonId, competitionId, applySiblingRows, recordSiblingRow]);


  // Shared by both resync triggers below: bumping resyncToken re-runs the
  // main fetch effect above, forcing a fresh DDS snapshot + resubscribe.
  const triggerResync = useCallback(() => setResyncToken((n) => n + 1), []);

  useTabResync(triggerResync, { minHiddenMs: 5_000 });

  // Network-return resync: tab-resync only fires on hidden→visible, so a
  // pure network drop with the tab staying focused the whole time (e.g.
  // Wi-Fi blip) never bumps resyncToken — the graphql-ws subscription can
  // sit silently dead until something else forces a refetch. Mirrors the
  // same `online`-event recovery used by @bb/event-service's STOMP client.
  useEffect(() => {
    window.addEventListener('online', triggerResync);
    return () => window.removeEventListener('online', triggerResync);
  }, [triggerResync]);

  // Derived Data
  const fixture = useMemo(
    () => fixtureState?.fixture?.[0] ?? null,
    [fixtureState],
  );
  const result = useMemo(() => {
    if (!fixtureState) return null;

    // Top-level paths are scoped to the current fixture (the query filters
    // by fixture_id), so [0] is safe there. The nested season path holds
    // ALL fixtures in the season — we MUST match by fixture_id, otherwise
    // an absent fixture_result for the current fixture silently borrows
    // the first sibling's score (e.g. shows 1-1 from a different match).
    const matchingSeasonFixture = fixtureId
      ? (fixtureState.season?.[0]?.fixture || []).find((f) => f?.fixture_id === fixtureId)
      : null;
    const seasonFixtureResult = matchingSeasonFixture?.fixture_result;

    return (
      fixtureState.fixture_result?.[0] ||
      fixtureState.fixture?.[0]?.fixture_result?.[0] ||
      (fixtureState.fixture?.[0]?.fixture_result && !Array.isArray(fixtureState.fixture?.[0]?.fixture_result) ? fixtureState.fixture?.[0]?.fixture_result : null) ||
      (Array.isArray(seasonFixtureResult) ? seasonFixtureResult[0] : null) ||
      (seasonFixtureResult && !Array.isArray(seasonFixtureResult) ? seasonFixtureResult : null) ||
      null
    );
  }, [fixtureState, fixtureId]);
  const homeTeam = useMemo(() => fixture?.home_team?.[0] ?? null, [fixture]);
  const awayTeam = useMemo(() => fixture?.away_team?.[0] ?? null, [fixture]);

  const updateFixtureState = useCallback((patch) => {
    if (patch?.collection === 'fixture_result' && patch?.data) {
      // fixture_result needs special handling: persist score optimistically via localScoreRef.
      // Narrow update — shallow-spread only the touched path. structuredClone(prev) used to
      // run on every patch (P4), wasting work on subtrees nothing read.
      setFixtureState((prev) => {
        const prevSafe = prev && typeof prev === 'object' ? prev : {};
        const prevFr0 = prevSafe.fixture_result?.[0];
        const baseFr =
          prevFr0 && typeof prevFr0 === 'object'
            ? prevFr0
            : { home_team_score: 0, away_team_score: 0 };
        const mergedFr = { ...baseFr, ...patch.data };
        localScoreRef.current = {
          home_team_score: mergedFr.home_team_score,
          away_team_score: mergedFr.away_team_score,
          timestamp: Date.now(),
        };
        return {
          ...prevSafe,
          fixture_result: [mergedFr, ...(prevSafe.fixture_result?.slice(1) || [])],
        };
      });

      // Also feed through the library's handlePatch so its internal currentData
      // stays in sync — otherwise the next unrelated subscription update (e.g. yellow card)
      // would broadcast stale scores from the library's outdated closure.
      if (patchFnRef.current) {
        patchFnRef.current(patch);
      }
      return;
    }

    if (patch?.collection === 'fixture' && patch?.data) {
      // Narrow update (P4): only the touched fixture[0] and its nested
      // season mirror get new identity. Other branches (home_team / away_team /
      // event / lineup / staff) keep their old refs so memos that depend on
      // them don't re-fire.
      setFixtureState((prev) => {
        const prevSafe = prev && typeof prev === 'object' ? prev : {};
        const prevFx0 = prevSafe.fixture?.[0];
        const mergedFx0 = {
          ...(prevFx0 && typeof prevFx0 === 'object' ? prevFx0 : {}),
          ...patch.data,
        };
        let next = {
          ...prevSafe,
          fixture: [mergedFx0, ...(prevSafe.fixture?.slice(1) || [])],
        };
        const nestedFx0 = prevSafe.season?.[0]?.fixture?.[0];
        if (nestedFx0 && typeof nestedFx0 === 'object') {
          const mergedNested = { ...nestedFx0, ...patch.data };
          next = {
            ...next,
            season: [
              {
                ...prevSafe.season[0],
                fixture: [mergedNested, ...prevSafe.season[0].fixture.slice(1)],
              },
              ...prevSafe.season.slice(1),
            ],
          };
        }
        return next;
      });
      if (patchFnRef.current) {
        try { patchFnRef.current(patch); } catch (e) { console.warn('dds-client applyStaticDataPatch failed on fixture', e); }
      }
      return;
    }

    // Normalize: the local UI cleanup fires for either case so a stray
    // uppercase caller still wipes the row from the visible state.
    if (
      String(patch?.op ?? '').toLowerCase() === 'delete' &&
      patch?.collection === 'event' &&
      patch?.data?.event_id
    ) {
      const deletedId = patch.data.event_id;
      // Tombstone the id so any subsequent subscription re-publish (DWS
      // sometimes re-emits the event before the source-side delete has
      // propagated) gets filtered out before it lands on state.
      removedEventIdsRef.current.add(deletedId);
      // Narrow update — only the affected event arrays + their containers
      // get new identity (P4).
      setFixtureState((prev) => {
        const prevSafe = prev && typeof prev === 'object' ? prev : {};
        let next = prevSafe;
        const prevFx0 = prevSafe.fixture?.[0];
        if (prevFx0?.event) {
          const filtered = prevFx0.event.filter((e) => e.event_id !== deletedId);
          if (filtered.length !== prevFx0.event.length) {
            next = {
              ...next,
              fixture: [
                { ...prevFx0, event: filtered },
                ...prevSafe.fixture.slice(1),
              ],
            };
          }
        }
        const nestedFx0 = prevSafe.season?.[0]?.fixture?.[0];
        if (nestedFx0?.event) {
          const filteredNested = nestedFx0.event.filter((e) => e.event_id !== deletedId);
          if (filteredNested.length !== nestedFx0.event.length) {
            next = {
              ...next,
              season: [
                {
                  ...prevSafe.season[0],
                  fixture: [
                    { ...nestedFx0, event: filteredNested },
                    ...prevSafe.season[0].fixture.slice(1),
                  ],
                },
                ...prevSafe.season.slice(1),
              ],
            };
          }
        }
        return next;
      });
      if (patchFnRef.current) {
        try { patchFnRef.current(patch); } catch (e) { console.warn('dds-client applyStaticDataPatch failed on DELETE', e); }
      }
      return;
    }

    // Optimistic event update — patches the matching event row in both
    // fixture[0].event and season[0].fixture[0].event so the timeline
    // reflects the change synchronously, before DDS publishes eventsUpdated.
    if (
      String(patch?.op ?? '').toLowerCase() === 'update' &&
      patch?.collection === 'event'
    ) {
      const targetId = patch?.key?.event_id ?? patch?.data?.event_id;
      if (!targetId) return;
      const patchData = patch.data || {};
      setFixtureState((prev) => {
        const prevSafe = prev && typeof prev === 'object' ? prev : {};
        let next = prevSafe;
        const mergeEvent = (events) => {
          let changed = false;
          const out = (events || []).map((ev) => {
            if (ev.event_id !== targetId) return ev;
            changed = true;
            return { ...ev, ...patchData };
          });
          return changed ? out : null;
        };
        const prevFx0 = prevSafe.fixture?.[0];
        if (prevFx0?.event) {
          const merged = mergeEvent(prevFx0.event);
          if (merged) {
            next = {
              ...next,
              fixture: [
                { ...prevFx0, event: merged },
                ...prevSafe.fixture.slice(1),
              ],
            };
          }
        }
        const nestedFx0 = prevSafe.season?.[0]?.fixture?.[0];
        if (nestedFx0?.event) {
          const mergedNested = mergeEvent(nestedFx0.event);
          if (mergedNested) {
            next = {
              ...next,
              season: [
                {
                  ...prevSafe.season[0],
                  fixture: [
                    { ...nestedFx0, event: mergedNested },
                    ...prevSafe.season[0].fixture.slice(1),
                  ],
                },
                ...prevSafe.season.slice(1),
              ],
            };
          }
        }
        return next;
      });
      return;
    }

    if (patchFnRef.current) {
      patchFnRef.current(patch);
    } else {
      setFixtureState((prev) => applyStaticDataPatch(prev, patch));
    }
  }, []);

  /** Events from DDS (subscribed via getFixtureStaticFullWithSubscriptions). Real-time list for timeline. */
  const events = useMemo(
    () => fixture?.event ?? [],
    [fixture?.event],
  );

  // OPUS-1403 — season.all_fixtures, fetched separately from the main
  // getFixtureStaticFullWithSubscriptions call above (that preset is a
  // fixed-signature shared-library orchestrator with no way to inject extra
  // keys — extending it would mean editing @aelive/dds-client itself,
  // affecting every consumer of the canonical preset, not just this app).
  //
  // Merged onto the EXPOSED fixtureState.season[0].all_fixtures as a pure
  // derived value (useMemo over the raw fixtureState + allFixtures), not via
  // setFixtureState: every internal read/write above (score guard, event
  // tombstoning, patch handlers, the fixture/result/homeTeam/awayTeam memos)
  // keeps operating on the raw, un-merged fixtureState exactly as before —
  // this merge has no way to interact with any of that. Deriving fresh on
  // every render also sidesteps the staleness a stored merge would have: a
  // setFixtureState-based merge keyed only on allFixtures changing would go
  // missing right after a fixture switch resets fixtureState to {} (nothing
  // would re-trigger the merge, since allFixtures itself wouldn't have
  // changed) — a plain derived value just recomputes from whatever the
  // current inputs are, every time, so it can't go stale that way.
  const allFixturesPreset = useMemo(() => {
    if (!competitionId || !seasonId) return null;
    const preset = seasonAllFixturesPreset({
      competition_id: competitionId,
      season_id: seasonId,
      limit: SEASON_ALL_FIXTURES_LIMIT,
      includeVenue: true,
    });
    // The token must live ON the preset object, not merely in the deps below:
    // useDDSGetData keys its effect on JSON.stringify(preset), so a new object
    // identity with identical contents would NOT re-read. It is a cache-buster,
    // not a query field — DDS ignores unknown top-level keys on the preset.
    return allFixturesToken ? { ...preset, __refetchToken: allFixturesToken } : preset;
  }, [competitionId, seasonId, allFixturesToken]);

  const { data: allFixturesData } = useDDSGetData(allFixturesPreset, {
    enabled: Boolean(competitionId && seasonId),
  });

  // Belt-and-braces against a cross-season leak into
  // fixtureStateWithAllFixtures, which feeds TXLive's live caption
  // data-binding context. useDDSGetData now clears `data` whenever the preset
  // changes, so allFixturesData can no longer hold the PREVIOUS season's rows
  // — but this check costs nothing and keeps the guarantee local: the
  // season_id the response itself carries (season.season_id is always
  // requested — see buildAllFixturesKeys in @aelive/dds-client) must match the
  // season we are asking about. Resolves to [] until the real fetch lands.
  const allFixtures = useMemo(() => {
    if (allFixturesData?.season?.[0]?.season_id !== seasonId) return [];
    return allFixturesData.season[0].all_fixtures ?? [];
  }, [allFixturesData, seasonId]);

  const fixtureStateWithAllFixtures = useMemo(() => {
    if (!fixtureState.season?.length) return fixtureState;
    return {
      ...fixtureState,
      season: [
        { ...fixtureState.season[0], all_fixtures: allFixtures },
        ...fixtureState.season.slice(1),
      ],
    };
  }, [fixtureState, allFixtures]);

  return {
    loading,
    fixtureState: fixtureStateWithAllFixtures,
    fixture,
    result,
    homeTeam,
    awayTeam,
    events,
    updateFixtureState,
    allFixtures,
  };
};

